import type { SyntheticEvent } from 'react';
import { pickEnum, searchableSource, type FormErrors, type OptionQuery } from '@eo/shared';

import type {
  CapacityReport,
  ClientReport,
  RoleHours,
  ClientListParams,
  EntryListParams,
  ExcessBill,
  GenerateExcessBillBody,
  ListParams,
  LoadAcrossPortfolios,
  PortfolioTotals,
  StaffReport,
} from './api/types';
import {
  CLIENT_GRID_PREFIX,
  CLIENT_ORDER_SORT,
  DETAIL_PANES,
  LOG_GRID_PREFIX,
  TEAM_LOAD_SCOPES,
  UNCATEGORIZED,
} from './constants';
import type {
  CategoryTotal,
  CompositionSegment,
  ClientDelivery,
  DeliveryRow,
  DetailPane,
  CapacityState,
  ExcessAdditionValues,
  ExcessDraft,
  ExcessLine,
  ExcessTotals,
  LogFilters,
  OptionPickers,
  SearchFilterSource,
  Selection,
  TeamCategoryRow,
  ClientOrder,
  TeamLoadRow,
  TeamLoadScope,
  RingArc,
} from './types';

// One grid's page, sort and search, read from its prefixed URL params.
export function gridListParams(
  prefix: string,
  query: Record<string, string | undefined>,
): ListParams {
  const page = Number(query[`${prefix}page`]);
  return {
    page: Number.isInteger(page) && page > 0 ? page : undefined,
    sort: query[`${prefix}sort`] || undefined,
    search: query[`${prefix}search`] || undefined,
  };
}

export function clientListParams(query: Record<string, string | undefined>): ClientListParams {
  const list = gridListParams(CLIENT_GRID_PREFIX, query);
  return {
    clientId: query[`${CLIENT_GRID_PREFIX}client`] || undefined,
    ...list,
    sort: list.sort ?? CLIENT_ORDER_SORT.attention,
  };
}

// The client or person whose detail is open; a client wins if a URL names both.
export function parseSelection(query: Record<string, string | undefined>): Selection | null {
  if (query.client) return { kind: 'client', id: query.client };
  if (query.staff) return { kind: 'member', id: query.staff };
  return null;
}

export function parseDetailPane(raw: string | undefined): DetailPane {
  return pickEnum(DETAIL_PANES, raw ?? null, 'summary');
}

export function clientOrderOf(sort: string | null): ClientOrder {
  return sort === CLIENT_ORDER_SORT.name ? 'name' : 'attention';
}

export function parseTeamLoadScope(raw: string | null): TeamLoadScope {
  return pickEnum(TEAM_LOAD_SCOPES, raw, 'portfolio');
}

// The log narrows to the open selection, then to whoever or whichever client
// was picked from its lists, plus its own grid params.
export function entryListParams(
  selection: Selection,
  query: Record<string, string | undefined>,
): EntryListParams {
  const billable = query[`${LOG_GRID_PREFIX}billable`];
  return {
    clientId:
      selection.kind === 'client' ? selection.id : query[`${LOG_GRID_PREFIX}client`] || undefined,
    userId:
      selection.kind === 'member' ? selection.id : query[`${LOG_GRID_PREFIX}member`] || undefined,
    billable: billable === 'billable' ? true : billable === 'nonbillable' ? false : undefined,
    ...gridListParams(LOG_GRID_PREFIX, query),
  };
}

// The portfolio a client filter searches within: its owner, or every client.
export function portfolioFilters(report: CapacityReport): OptionQuery['filters'] {
  return report.scope.ownerUserId ? { ownerUserId: report.scope.ownerUserId } : undefined;
}

export function clientFilterSource(
  state: CapacityState,
  report: CapacityReport,
  pickedId: string | null,
  onQueryOptions: OptionPickers['onQueryOptions'],
): SearchFilterSource {
  return {
    options: state.options.clients.items.map((c) => ({ value: c.id, label: c.name })),
    selectedLabel: report.clients.find((c) => c.clientId === pickedId)?.name,
    ...searchableSource(state.options.clients, (q) =>
      onQueryOptions({ source: 'clients', ...q, filters: portfolioFilters(report) }),
    ),
  };
}

export function personFilterSource(
  state: CapacityState,
  report: CapacityReport,
  pickedId: string | null,
  onQueryOptions: OptionPickers['onQueryOptions'],
): SearchFilterSource {
  return {
    options: state.options.people.items.map((p) => ({ value: p.userId, label: p.name })),
    selectedLabel: report.users.find((u) => u.userId === pickedId)?.name,
    ...searchableSource(state.options.people, (q) => onQueryOptions({ source: 'people', ...q })),
  };
}

// The side already open has no filter: a client's log narrows by person, a
// person's log by client.
export function logFilters(
  state: CapacityState,
  report: CapacityReport,
  selection: Selection,
  query: URLSearchParams,
  onQueryOptions: OptionPickers['onQueryOptions'],
): LogFilters {
  const picked = (key: string) => query.get(`${LOG_GRID_PREFIX}${key}`);
  return selection.kind === 'client'
    ? { people: personFilterSource(state, report, picked('member'), onQueryOptions), clients: null }
    : {
        people: null,
        clients: clientFilterSource(state, report, picked('client'), onQueryOptions),
      };
}

export function selectionName(report: CapacityReport, selection: Selection): string {
  return selection.kind === 'client'
    ? (report.clients.find((c) => c.clientId === selection.id)?.name ?? 'Client')
    : (report.users.find((u) => u.userId === selection.id)?.name ?? 'Person');
}

// The URL that opens one client's or person's detail, closing whatever was open
// and starting its log afresh; everything else on the page stays as it was.
export function selectionSearch(current: URLSearchParams, next: Selection | null): string {
  const params = new URLSearchParams(current);
  for (const key of [...params.keys()]) {
    if (
      ['client', 'staff', 'pane', 'tab', 'view'].includes(key) ||
      key.startsWith(LOG_GRID_PREFIX)
    ) {
      params.delete(key);
    }
  }
  if (next) params.set(next.kind === 'client' ? 'client' : 'staff', next.id);
  return `?${params.toString()}`;
}

// The open client's time log, narrowed to one person on it.
export function personLogSearch(
  current: URLSearchParams,
  clientId: string,
  userId: string,
): string {
  const params = new URLSearchParams(selectionSearch(current, { kind: 'client', id: clientId }));
  params.set('pane', 'log');
  params.set(`${LOG_GRID_PREFIX}member`, userId);
  return `?${params.toString()}`;
}

export function portfolioName(report: CapacityReport): string | null {
  const owner = report.scope.ownerUserId;
  if (owner === null) return null;
  return (
    report.portfolios.find((p) => p.ownerUserId === owner)?.name ??
    report.users.find((u) => u.userId === owner)?.name ??
    null
  );
}

export function compositionSegments(
  totals: PortfolioTotals,
  bucket: 'fixed' | 'variable',
): CompositionSegment[] {
  const key = bucket === 'fixed' ? 'assignedFixed' : 'assignedVariable';
  return [
    { key: 'cfo', label: 'CFO', hours: totals.byRole.cfo[key] },
    { key: 'dataTeam', label: 'Data team', hours: totals.byRole.dataTeam[key] },
  ];
}

// Everyone with hours on the portfolio, or anywhere when looking across every
// portfolio; CFOs first, the most assigned on top.
export function teamLoadRows(
  users: StaffReport[],
  across: LoadAcrossPortfolios[],
  scope: TeamLoadScope,
): TeamLoadRow[] {
  const onPortfolio = new Set(users.map((u) => u.userId));
  const loads =
    scope === 'all'
      ? across
      : users.map((u) => ({ userId: u.userId, name: u.name, role: u.role, ...u.onPortfolio }));
  return loads
    .map((u) => ({
      ...u,
      role: u.role === 'data_team' ? ('dataTeam' as const) : ('cfo' as const),
      onPortfolio: onPortfolio.has(u.userId),
    }))
    .filter((r) => r.assigned > 0 || r.actual > 0)
    .sort(
      (a, b) =>
        Number(a.role === 'dataTeam') - Number(b.role === 'dataTeam') ||
        b.assigned - a.assigned ||
        a.name.localeCompare(b.name),
    );
}

// Arcs of a ring whose full turn is the capacity, or the commitment when that
// runs past it; lengths and offsets are percent of the turn.
export function ringArcs(capacity: number, logged: number, committed: number): RingArc[] {
  const scale = Math.max(capacity, committed, logged);
  const pct = (hours: number) => (scale > 0 ? (hours / scale) * 100 : 0);
  const arcs: RingArc[] = [
    { key: 'logged', offset: 0, length: pct(logged) },
    { key: 'remaining', offset: pct(logged), length: pct(Math.max(0, committed - logged)) },
    { key: 'over', offset: pct(capacity), length: pct(Math.max(0, committed - capacity)) },
  ];
  return arcs.filter((arc) => arc.length > 0);
}

// The CFO's side and the Data team's, each logged against what it holds.
export function roleDeliveryRows(byRole: { cfo: RoleHours; dataTeam: RoleHours }): DeliveryRow[] {
  const row = (key: 'cfo' | 'dataTeam', label: string): DeliveryRow => ({
    key,
    label,
    role: key,
    held: byRole[key].assignedFixed + byRole[key].assignedVariable,
    logged: byRole[key].actualFixed + byRole[key].actualVariable,
  });
  return [row('cfo', 'CFO'), row('dataTeam', 'Data team')];
}

// A client's total, its two sides, and each Data team member on it.
export function clientDelivery(client: ClientReport, users: StaffReport[]): ClientDelivery {
  const roleOf = (userId: string) => users.find((u) => u.userId === userId)?.role;
  const people = client.assignments
    .filter((a) => roleOf(a.userId) === 'data_team')
    .map((a) => ({
      key: a.userId,
      label: users.find((u) => u.userId === a.userId)?.name ?? 'Former member',
      role: 'dataTeam' as const,
      held: a.fixedHours + a.variableHours,
      logged: a.actualHours,
    }));
  const [cfo, dataTeam] = roleDeliveryRows(client.byRole);
  return {
    total: { held: cfo.held + dataTeam.held, logged: cfo.logged + dataTeam.logged },
    buckets: (['fixed', 'variable'] as const).map((key) => {
      const side = key === 'fixed' ? 'Fixed' : 'Variable';
      const { cfo: c, dataTeam: d } = client.byRole;
      return {
        key,
        label: side,
        held: c[`assigned${side}`] + d[`assigned${side}`],
        logged: { cfo: c[`actual${side}`], dataTeam: d[`actual${side}`] },
      };
    }),
    owner: client.ownerUserId
      ? {
          userId: client.ownerUserId,
          name: users.find((u) => u.userId === client.ownerUserId)?.name ?? 'Owner',
        }
      : null,
    cfo,
    dataTeam,
    people,
  };
}

// A radial tick across the ring at a point of its turn, for an SVG drawn
// rotated so the turn starts at the top.
export function ringTick(hours: number, scale: number) {
  const angle = (scale > 0 ? Math.min(1, hours / scale) : 0) * 2 * Math.PI;
  return {
    x1: 60 + 43 * Math.cos(angle),
    y1: 60 + 43 * Math.sin(angle),
    x2: 60 + 57 * Math.cos(angle),
    y2: 60 + 57 * Math.sin(angle),
  };
}

// A client row links to its detail, and React carries events out of inputs and
// portalled popovers to it, so an editable cell stops them.
export function stopRowEvent(e: SyntheticEvent): void {
  e.stopPropagation();
}

export function barPercent(hours: number, scale: number): string {
  return `${scale > 0 ? (hours / scale) * 100 : 0}%`;
}

export function monthShare(progress: CapacityReport['progress']): number {
  return progress.workingDays > 0 ? progress.elapsedWorkingDays / progress.workingDays : 0;
}

export function monthEndOf(month: string): string {
  const [year, mon] = month.split('-').map(Number);
  return new Date(Date.UTC(year, mon, 0)).toISOString().slice(0, 10);
}

// "Aug 1–31" within one month, "Jul 15 – Aug 14" across two.
export function formatDateRange(start: string, end: string): string {
  const day = (iso: string) => Number(iso.slice(8, 10));
  const mon = (iso: string) =>
    new Date(`${iso}T00:00:00Z`).toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
  return start.slice(0, 7) === end.slice(0, 7)
    ? `${mon(start)} ${day(start)}–${day(end)}`
    : `${mon(start)} ${day(start)} – ${mon(end)} ${day(end)}`;
}

export function formatHours(value: number): string {
  return `${value.toFixed(2)}h`;
}

export function utilizationPct(actual: number, budget: number): number {
  return budget > 0 ? Math.round((actual / budget) * 100) : 0;
}

export function teamCategoryRows(member: StaffReport): TeamCategoryRow[] {
  const share = (pct: number) => (member.capacity.netHours * pct) / 100;
  const split = member.split;
  return [
    {
      id: 'csd',
      label: 'Client Service Delivery',
      pct: split?.csd,
      actual: member.categoryActuals.csd,
    },
    { id: 'rnd', label: 'R&D', pct: split?.rnd, actual: member.categoryActuals.rnd },
    {
      id: 'opsAdmin',
      label: 'Ops & Admin',
      pct: split?.opsAdmin,
      actual: member.categoryActuals.opsAdmin,
    },
  ].map(({ id, label, pct, actual }) => ({
    id,
    label,
    splitPct: pct ?? null,
    capacityShare: pct === undefined ? null : share(pct),
    actual,
  }));
}

// The report is a cache now, so its age is part of the reading. Anything older
// than a day states the date rather than a widening minute count.
export function freshnessLabel(syncedAt: string | null, now: number = Date.now()): string {
  if (!syncedAt) return 'Never synced';
  const ageMs = now - Date.parse(syncedAt);
  if (!Number.isFinite(ageMs) || ageMs < 0) return 'Synced just now';

  const minutes = Math.floor(ageMs / 60_000);
  if (minutes < 1) return 'Synced just now';
  if (minutes < 60) return `Synced ${minutes} minute${minutes === 1 ? '' : 's'} ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Synced ${hours} hour${hours === 1 ? '' : 's'} ago`;

  return `Synced ${new Date(syncedAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })}`;
}

const roundCents = (value: number) => Math.round(value * 100) / 100;

export function quarterHours(value: number): number {
  return Math.max(0.25, Math.round(value * 4) / 4);
}

export function emptyExcessDraft(bill: ExcessBill): ExcessDraft {
  return { hours: {}, archived: {}, additions: [], writeoffHours: bill.writeoffHours };
}

// The bill as the CFO is editing it: the generated entries with the draft laid
// over them, then the entries added since.
export function excessLines(bill: ExcessBill, draft: ExcessDraft): ExcessLine[] {
  const entries = bill.entries.map((entry) => {
    const hours = draft.hours[entry.entryId] ?? entry.hours;
    return {
      key: entry.entryId,
      member: entry.member,
      taskName: entry.taskName,
      category: entry.category,
      startTs: entry.startTs,
      hours,
      archived: draft.archived[entry.entryId] ?? entry.archived,
      edited: hours !== entry.hours,
      added: false,
    };
  });
  const added = draft.additions.map((addition) => ({
    key: addition.key,
    member: 'You',
    taskName: addition.taskName,
    category: null,
    startTs: Date.parse(`${addition.date}T12:00:00Z`),
    hours: draft.hours[addition.key] ?? addition.hours,
    archived: false,
    edited: false,
    added: true,
  }));
  return [...entries, ...added];
}

// Excess is what the variable hours run past the plan, less any courtesy
// write-off; fixed hours never reach the bill.
export function excessTotals(
  lines: ExcessLine[],
  bill: Pick<ExcessBill, 'budgetHours' | 'rate'>,
  writeoffHours: number,
): ExcessTotals {
  const variable = roundCents(
    lines.filter((line) => !line.archived).reduce((sum, line) => sum + line.hours, 0),
  );
  const excess = roundCents(Math.max(0, variable - bill.budgetHours - writeoffHours));
  return {
    variable,
    included: bill.budgetHours,
    writeoff: writeoffHours,
    excess,
    rate: bill.rate,
    amount: roundCents(excess * bill.rate),
  };
}

export function categoryTotals(lines: ExcessLine[]): CategoryTotal[] {
  const byCategory = new Map<string, number>();
  for (const line of lines) {
    if (line.archived) continue;
    const category = line.category ?? UNCATEGORIZED;
    byCategory.set(category, (byCategory.get(category) ?? 0) + line.hours);
  }
  return [...byCategory]
    .map(([category, hours]) => ({ category, hours: roundCents(hours) }))
    .sort((a, b) => b.hours - a.hours || a.category.localeCompare(b.category));
}

// The editor's groups: every line under its category, archived ones included
// so they can be restored. `order` is fixed by the caller rather than worked
// out from the draft, so a line never jumps while its hours are being stepped.
// The report's order for the bill as it was opened.
export function billCategoryOrder(bill: ExcessBill): string[] {
  return categoryTotals(excessLines(bill, emptyExcessDraft(bill))).map((c) => c.category);
}

export function excessGroups(
  lines: ExcessLine[],
  order: string[],
): Array<{ category: string; hours: number; lines: ExcessLine[] }> {
  const groups = new Map<string, ExcessLine[]>();
  for (const line of lines) {
    const category = line.category ?? UNCATEGORIZED;
    groups.set(category, [...(groups.get(category) ?? []), line]);
  }
  const rank = (category: string) => {
    const index = order.indexOf(category);
    return index === -1 ? order.length : index;
  };
  return [...groups]
    .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
    .map(([category, rows]) => ({
      category,
      hours: roundCents(rows.filter((l) => !l.archived).reduce((s, l) => s + l.hours, 0)),
      lines: rows.sort((a, b) => a.startTs - b.startTs),
    }));
}

function editsOf(bill: ExcessBill, draft: ExcessDraft) {
  return bill.entries
    .filter((entry) => (draft.hours[entry.entryId] ?? entry.hours) !== entry.hours)
    .map((entry) => ({ entryId: entry.entryId, hours: draft.hours[entry.entryId] }));
}

export function pendingChangeCount(bill: ExcessBill, draft: ExcessDraft): number {
  const archived = bill.entries.filter(
    (entry) => (draft.archived[entry.entryId] ?? entry.archived) !== entry.archived,
  );
  return (
    editsOf(bill, draft).length +
    archived.length +
    draft.additions.length +
    (draft.writeoffHours !== bill.writeoffHours ? 1 : 0)
  );
}

export function generateExcessBody(bill: ExcessBill, draft: ExcessDraft): GenerateExcessBillBody {
  return {
    clientId: bill.clientId,
    month: bill.month,
    writeoffHours: draft.writeoffHours,
    excludedEntryIds: bill.entries
      .filter((entry) => draft.archived[entry.entryId] ?? entry.archived)
      .map((entry) => entry.entryId),
    edits: editsOf(bill, draft),
    additions: draft.additions.map(({ key, taskName: _taskName, ...addition }) => ({
      ...addition,
      hours: draft.hours[key] ?? addition.hours,
    })),
  };
}

export function billMonthLabel(month: string): string {
  return new Date(`${month}T12:00:00Z`).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function formatBillHours(value: number): string {
  return value.toFixed(2);
}

export function formatUsd(value: number): string {
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
  });
}

export function validateExcessAddition(month: string) {
  return (values: ExcessAdditionValues): FormErrors<ExcessAdditionValues> => {
    const errors: FormErrors<ExcessAdditionValues> = {};
    if (!values.taskId) errors.taskId = 'Pick one of the client’s tasks.';
    if (!values.date.startsWith(month.slice(0, 8))) {
      errors.date = `Pick a day in ${billMonthLabel(month)}.`;
    }
    const hours = Number(values.hours);
    if (!(hours > 0 && hours <= 24)) errors.hours = 'Enter between 0.25 and 24 hours.';
    return errors;
  };
}
